// =============================================================================
// MenuView.ts — Menu "boca de urna" (redesign da mesa de salas): a rota dos
// 30 segundos (nome + ENTRAR + CRIAR SALA) fica cravada no topo do painel;
// o resto vira duas abas — COMITÊS ABERTOS (mural + código + dossiês) e
// ELEIÇÕES OFICIAIS (do dia / da semana).
// =============================================================================
import type { OpenRoomInfo } from '../../../shared/protocol';
import { DIFFICULTY_TABLE } from '../../../shared/difficulty';
import type { EleicaoMode } from '../../../shared/eleicao';
import { $, $input } from './dom';
import { roomPixelIcon } from './pixelIcons';

const NAME_KEY = 'bs_name';

// A busca do mural só aparece quando a lista fica longa o bastante pra
// precisar dela; abaixo disso é só mais um campo poluindo a aba
const BUSCA_A_PARTIR_DE = 9;

// Nome de eleitor entra em innerHTML: escapa antes que alguém se eleja com
// <script> no título de eleitor
const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

// Faixas de sinal da apuração: verde = fibra, laranja = 4G rural, vermelho =
// internet discada de gabinete
const pingTier = (ms: number): { cls: string; barras: number } =>
  ms <= 50 ? { cls: 'ping-bom', barras: 4 }
    : ms <= 90 ? { cls: 'ping-medio', barras: 3 }
      : { cls: 'ping-ruim', barras: 2 };

export class MenuView {
  private lastRooms: OpenRoomInfo[] = [];
  private lastPing = 0;
  private onJoin: (code: string) => void = () => { /* setado no 1º render */ };
  private filtro = '';

  constructor(handlers: {
    onCreate: () => void;
    onJoinCode: (code: string) => void;
    onEleicao: (mode: EleicaoMode) => void;   // Eleição do Dia/da Semana (#101/#97)
    onBack: () => void;                        // VOLTAR: devolve pro menu principal (title)
  }) {
    $input('input-name').value = localStorage.getItem(NAME_KEY) ?? '';

    // VOLTAR ao menu principal (ranking, conquistas, conta): o mural não é
    // beco sem saída — dá pra subir de volta pra tela de título
    $('btn-menu-back').onclick = () => handlers.onBack();

    // Abas: um assunto por vez (comitês | eleições oficiais)
    this.wireAba('aba-btn-comites', 'aba-comites');
    this.wireAba('aba-btn-eleicoes', 'aba-eleicoes');

    // Busca do mural: filtra as salas pelo nome do comitê (ou pelo código)
    // sem ir ao servidor — repinta a lista com o que já temos em mãos
    $input('input-search').addEventListener('input', (e) => {
      this.filtro = (e.target as HTMLInputElement).value.trim().toLowerCase();
      this.paintRooms();
    });
    $('btn-create').onclick = () => handlers.onCreate();
    $('btn-join').onclick = () => {
      const code = $input('input-code').value.trim().toUpperCase();
      if (code.length !== 6) {
        this.setError('Código tem 6 caracteres, igual dígito de candidato... quase.');
        return;
      }
      handlers.onJoinCode(code);
    };

    // Eleições com seed (#101/#97): partidas solo do período, direto da aba
    $('btn-eleicao-diaria').onclick = () => handlers.onEleicao('diaria');
    $('btn-eleicao-semanal').onclick = () => handlers.onEleicao('semanal');

    // ENTRAR amarelo = furar a fila: cai no primeiro comitê aberto do mural.
    // Sem comitê aberto? Faça como todo político: funde o seu.
    $('btn-quickjoin').onclick = () => {
      const alvo = this.lastRooms[0];
      if (alvo) { handlers.onJoinCode(alvo.code); return; }
      this.setError('Nenhum comitê com vaga no momento. Funde o seu próprio partido: CRIAR SALA.');
    };

    // A setinha ↵ do botão não é decoração: Enter faz o que promete
    $input('input-name').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') $('btn-quickjoin').click();
    });
    $input('input-code').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') $('btn-join').click();
    });
  }

  isActive(): boolean {
    return $('screen-menu').classList.contains('active');
  }

  playerName(): string {
    const n = $input('input-name').value.trim() || 'Eleitor Anônimo';
    localStorage.setItem(NAME_KEY, n);
    return n;
  }

  setError(msg: string): void {
    $('menu-error').textContent = msg;
  }

  // Mural de comitês abertos (atualiza enquanto o menu estiver na tela).
  // pingMs é o RTT do PRÓPRIO cliente: todas as salas moram no mesmo
  // servidor, então a latência é a mesma pra qualquer comitê.
  renderRoomList(rooms: OpenRoomInfo[], pingMs: number, onJoin: (code: string) => void): void {
    this.lastRooms = rooms;
    this.lastPing = pingMs;
    this.onJoin = onJoin;
    this.paintRooms();
  }

  // Liga o par botão de aba → seção; a troca é só classe + hidden
  private wireAba(btnId: string, secaoId: string): void {
    $(btnId).onclick = () => {
      for (const b of document.querySelectorAll<HTMLButtonElement>('.urna-aba')) {
        const ativa = b.id === btnId;
        b.classList.toggle('on', ativa);
        b.setAttribute('aria-selected', String(ativa));
      }
      for (const s of document.querySelectorAll<HTMLElement>('.urna-secao')) {
        s.hidden = s.id !== secaoId;
        s.classList.toggle('on', s.id === secaoId);
      }
    };
  }

  // Repinta o mural a partir do último snapshot, aplicando a busca por nome.
  // O badge da aba reflete o total de comitês abertos (não o filtrado).
  private paintRooms(): void {
    const rooms = this.filtro
      ? this.lastRooms.filter((r) =>
        r.host.toLowerCase().includes(this.filtro) || r.code.toLowerCase().includes(this.filtro))
      : this.lastRooms;
    const onJoin = this.onJoin;

    $('room-count').textContent = String(this.lastRooms.length);
    // A busca só entra em campo quando o mural cresce; some junto com a lista
    $('mural-busca').hidden = this.lastRooms.length < BUSCA_A_PARTIR_DE;
    this.paintPing();

    const box = $('room-list');
    box.innerHTML = '';
    if (!rooms.length) {
      box.innerHTML = this.lastRooms.length
        ? '<p class="zona-vazia">Nenhum comitê com esse nome. Tente outro ou crie o seu!</p>'
        : '<p class="zona-vazia">Nenhum comitê aberto no momento. Crie o seu!</p>';
      return;
    }

    for (const r of rooms) {
      const diff = DIFFICULTY_TABLE[r.difficulty];
      const cheia = r.players >= r.max;
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'sala-row';
      row.title = `Entrar no comitê ${r.code}`;
      row.innerHTML = `
        <span class="sala-nome">${roomPixelIcon(r.code)}<b>Comitê de ${esc(r.host)}</b><small class="sala-cod">#${r.code}</small></span>
        <span class="sala-vagas${cheia ? ' cheia' : ''}">${r.players}/${r.max}</span>
        <span class="sala-diff diff-${r.difficulty}">${diff?.nome ?? r.difficulty}</span>`;
      row.onclick = () => onJoin(r.code);
      box.appendChild(row);
    }
  }

  // Ping do cliente no statuszinho do mural: barrinhas de sinal + ms
  private paintPing(): void {
    const el = $('mural-ping');
    if (this.lastPing <= 0) { el.innerHTML = ''; return; }
    const tier = pingTier(this.lastPing);
    el.className = `sala-ping ${tier.cls}`;
    el.innerHTML = `<span class="barras" aria-hidden="true">${
      [1, 2, 3, 4].map((i) => `<i${i <= tier.barras ? ' class="on"' : ''}></i>`).join('')
    }</span><span class="ms">${this.lastPing} ms</span>`;
  }
}
